import { memo } from 'react';
import { AlertCircle, X } from 'lucide-react';
import { FILE_KIND_META, IconButton, cn, fileKindFor } from '@cyphex/ui';
import { formatBytes } from '../../lib/upload';
import type { ComposerAttachment } from '../../types';

interface AttachmentTrayProps {
  attachments: readonly ComposerAttachment[];
  onRemove: (id: string) => void;
}

/** Pending uploads with a live progress bar. Object URLs are revoked on unmount. */
export const AttachmentTray = memo(function AttachmentTray({ attachments, onRemove }: AttachmentTrayProps) {
  if (attachments.length === 0) return null;

  return (
    <ul className="flex flex-wrap gap-2 px-3 pb-2">
      {attachments.map((item) => {
        const meta = FILE_KIND_META[fileKindFor(item.name)];
        const Glyph = meta.icon;
        return (
          <li
            key={item.id}
            className="relative flex w-56 items-center gap-2.5 overflow-hidden rounded-lg border border-border bg-card p-2"
          >
            {item.objectUrl ? (
              <img
                src={item.objectUrl}
                alt=""
                width={36}
                height={36}
                className="h-9 w-9 shrink-0 rounded-md object-cover"
              />
            ) : (
              <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-md', meta.tileClass)}>
                <Glyph size={16} className={meta.colorClass} aria-hidden />
              </span>
            )}

            <span className="min-w-0 flex-1">
              <span className="block truncate text-base font-medium text-foreground">{item.name}</span>
              <span className="flex items-center gap-1 text-sm text-muted-foreground">
                {item.status === 'failed' ? (
                  <>
                    <AlertCircle size={14} className="text-destructive" aria-hidden />
                    <span className="text-destructive">{item.error ?? 'Failed'}</span>
                  </>
                ) : (
                  formatBytes(item.sizeBytes)
                )}
              </span>
            </span>

            <IconButton
              icon={X}
              label={`Remove ${item.name}`}
              size="xs"
              variant="ghost"
              onClick={() => onRemove(item.id)}
            />

            {item.status !== 'ready' && item.status !== 'failed' ? (
              <span
                style={{ width: `${item.progress}%` }}
                className="absolute inset-x-0 bottom-0 h-0.5 bg-primary transition-all duration-fast"
                aria-hidden
              />
            ) : null}
          </li>
        );
      })}
    </ul>
  );
});
